/**
 * Libellés d'options d'aéroport, partagés entre le front (construction des options
 * de l'autocomplete) et le serveur (ré-hydratation d'un devis enregistré).
 *
 * Deux niveaux bien distincts :
 * - `formatCityOptionLabel` / `formatAirportOptionLabel` : la chaîne **stockée** dans
 *   le form state, sérialisée dans l'URL et renvoyée au back. Elle doit rester
 *   auto-suffisante (la ville y figure) car elle est réaffichée hors contexte.
 * - `airportRowTitle` : ce qui est **affiché dans la liste**, où la ville est déjà
 *   portée par la ligne parente et n'a pas à être répétée.
 */
/** Préfixes descriptifs sans valeur informative dans une liste de résultats. */
const AIRPORT_NAME_NOISE = /^a[ée]roport\s+(?:international\s+)?(?:de\s+la\s+|de\s+|d'|du\s+|des\s+)?/i;
/** Les gares ferroviaires sont repérées par leur nom : le code IATA (X…) n'est pas fiable. */
export const isRailStationName = (airportName) => airportName !== null && /^gare\b/i.test(airportName);
/**
 * Libellé d'une ville : le nom seul.
 *
 * La recherche client ne propose plus que des villes — préciser « tous les
 * aéroports » n'informerait plus de rien puisqu'il n'existe plus d'alternative.
 * Attention : ce libellé n'est plus unique (Paris/France et Paris/États-Unis).
 * Ce qui identifie une option reste son code ville, jamais son libellé.
 */
export const formatCityOptionLabel = (cityName, iataCityCode) => cityName ?? iataCityCode;
/** Libellé stocké d'un aéroport : « Marseille Aéroport de Marseille-Provence (MRS) ». */
export const formatAirportOptionLabel = ({ airportName, cityName, iataCode, }) => {
    if (!airportName)
        return cityName ? `${cityName} (${iataCode})` : iataCode;
    if (!cityName)
        return `${airportName} (${iataCode})`;
    return `${cityName} ${airportName} (${iataCode})`;
};
/**
 * Titre de la ligne d'un aéroport dans la liste : on retire le préfixe « Aéroport de »,
 * pur bruit répété sur chaque ligne.
 * « Aéroport de Marseille-Provence » → « Marseille-Provence ».
 *
 * La capitale initiale est forcée : certains noms en base commencent en minuscule
 * (« gare Montparnasse »), ce qui saute aux yeux dans une liste alignée.
 */
export const airportRowTitle = (airportName, iataCode) => {
    if (!airportName || airportName === iataCode)
        return iataCode;
    const stripped = airportName.replace(AIRPORT_NAME_NOISE, '').trim();
    const title = stripped.length > 0 ? stripped : airportName;
    return title.charAt(0).toUpperCase() + title.slice(1);
};
/**
 * Nom de pays en français à partir d'un code ISO alpha-2 (« FR » → « France »).
 * `Intl.DisplayNames` évite une table de correspondance à maintenir ; on retombe sur
 * le code brut si l'environnement ou le code est inconnu.
 */
let regionDisplayNames;
export const countryLabelFromCode = (countryCode) => {
    if (!countryCode)
        return null;
    if (regionDisplayNames === undefined) {
        try {
            regionDisplayNames = new Intl.DisplayNames(['fr'], { type: 'region' });
        }
        catch {
            regionDisplayNames = null;
        }
    }
    if (!regionDisplayNames)
        return countryCode;
    try {
        return regionDisplayNames.of(countryCode.toUpperCase()) ?? countryCode;
    }
    catch {
        return countryCode;
    }
};
